import { StaticImageData } from 'next/image';
import { Modal } from '../Modal/Modal';
import { AppDownloadContent } from './components/AppDownloadContent';
import { HeadingVariant } from '../Heading/Heading';
import { ClickableVariant } from '../Clickables/variants';
import { APPDOWNLOADMODAL_HEADER, APPDOWNLOADMODAL_TEXT } from './constants/constants';

interface IProps {
  appDownloadSource: string;
  appDownloadSourceId?: string;
  children?: React.ReactNode;
  className?: string;
  closeButtonVariant?: ClickableVariant;
  image?: StaticImageData;
  imageMobile?: StaticImageData;
  imageWrapperClassName?: string;
  isModalOpen: boolean;
  modalHeader?: string;
  modalHeaderClassName?: string;
  modalHeaderVariant?: HeadingVariant;
  modalText?: string;
  onCloseModal: () => void;
  showQRCodeHint?: boolean;
  showTextOnMobile?: boolean;
  textWrapperClassName?: string;
  width?: string;
}

/**
 * Component for displaying the App Download Modal.
 *
 * @interface IProps
 * @property {string} [appDownloadSource='AppDownloadModal'] - Optional source identifier for the app download.
 * @property {string} [className] - Optional class name for the modal.
 * @property {StaticImageData} [image] - Optional image to display in the modal.
 * @property {StaticImageData} [imageMobile] - Optional image to display in the modal on mobile devices.
 * @property {string} [imageWrapperClassName] - Optional class name for the image wrapper.
 * @property {boolean} isModalOpen - Flag to determine if the modal is open.
 * @property {string} [modalHeader=APPDOWNLOADMODAL_HEADER] - Optional header text for the modal.
 * @property {HeadingVariant} [modalHeaderVariant] - Optional variant for the modal header.
 * @property {string} [modalText=APPDOWNLOADMODAL_TEXT] - Optional text content for the modal.
 * @property {() => void} onCloseModal - Function to call when the modal is closed.
 * @property {boolean} [showQRCodeHint] - Flag to determine if the QR code hint should be shown.
 * @property {boolean} [showTextOnMobile] - Flag to determine if the text should be shown on mobile devices.
 * @property {string} [textWrapperClassName] - Optional class name for the text wrapper.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const AppDownloadModal = ({
  appDownloadSource,
  appDownloadSourceId,
  children,
  className,
  closeButtonVariant = 'controlWhiteBg',
  image,
  imageMobile,
  imageWrapperClassName,
  isModalOpen,
  modalHeader = APPDOWNLOADMODAL_HEADER,
  modalHeaderClassName,
  modalHeaderVariant,
  modalText = APPDOWNLOADMODAL_TEXT,
  onCloseModal,
  showQRCodeHint,
  showTextOnMobile = false,
  textWrapperClassName,
  width = '900px',
}: IProps) => {
  return isModalOpen ? (
    <Modal
      isOpen
      onClose={onCloseModal}
      size="lg"
      closeButtonVariant={closeButtonVariant}
      className="mx-6 rounded-lg"
      style={{ width: width }}
    >
      {children ?? (
        <AppDownloadContent
          appDownloadContext="downloadModal"
          appDownloadSource={appDownloadSource}
          appDownloadSourceId={appDownloadSourceId}
          className={className}
          contentHeader={modalHeader}
          contentHeaderClassName={modalHeaderClassName}
          contentHeaderVariant={modalHeaderVariant}
          contentText={modalText}
          contentWrapperClassName={textWrapperClassName}
          image={image}
          imageMobile={imageMobile}
          imageWrapperClassName={imageWrapperClassName}
          showQRCodeHint={showQRCodeHint}
          showTextOnMobile={showTextOnMobile}
          testID="AppDownloadModal"
        />
      )}
    </Modal>
  ) : (
    <></>
  );
};
